import type { ReactNode } from "react";
import { useLocation } from "react-router-dom";
import { DataGridTableSkeleton, DetailFormSkeleton } from "./index";
import ListPageCardShell from "./ListPageCardShell";
import TeamPageShell from "./TeamPageShell";
import DocumentLibraryShell from "./DocumentLibraryShell";
import InspectionOrdersListShell from "./InspectionOrdersListShell";
import CompanyDashboardSkeleton from "../../pages/dashboard/components/CompanyDashboardSkeleton";
import WorkOrdersListSkeleton from "../../pages/workorders/components/WorkOrdersListSkeleton";
import OnboardingListSkeleton from "../../pages/onboarding/components/OnboardingListSkeleton";
import MasterDataRequestSkeleton from "../../pages/masterData/masterDataRequest/components/MasterDataRequestSkeleton";
import DocumentUploadSkeleton from "../Documents/DocumentUploadSkeleton";
import LocationMapSkeleton from "../../pages/locationMap/components/LocationMapSkeleton";
import TeamPageSkeleton from "../../pages/team/components/TeamPageSkeleton";
import BillingPageSkeleton from "../../pages/billing/components/BillingPageSkeleton";
import SubscriptionOverviewSkeleton from "../../pages/subscription/components/SubscriptionOverviewSkeleton";
import AiMarketplaceSkeleton from "../../pages/ai-marketplace/components/AiMarketplaceSkeleton";
import InspectionOrdersListSkeleton from "../../pages/InspectionOrders/components/InspectionOrdersListSkeleton";
import InspectorInspectionSkeleton from "../../pages/InspectionOrders/components/InspectorInspectionSkeleton";
import TaskListSectionSkeleton from "../../pages/obligations/components/TaskListSectionSkeleton";
import CompanySelectionSkeleton from "../../pages/partner/components/CompanySelectionSkeleton";
import AssetListSkeleton from "../../pages/assets/components/AssetListSkeleton";

function masterDataListFallback(searchPlaceholder: string, addButtonLabel: string): ReactNode {
  return (
    <ListPageCardShell searchPlaceholder={searchPlaceholder} addButtonLabel={addButtonLabel}>
      <DataGridTableSkeleton height={500} rowCount={10} />
    </ListPageCardShell>
  );
}

export function useRouteSuspenseFallback(): ReactNode {
  const { pathname } = useLocation();

  if (pathname.startsWith("/inspection-orders/approver/")) {
    return <InspectorInspectionSkeleton />;
  }
  if (pathname.startsWith("/inspection-orders/inspector/")) {
    return <InspectorInspectionSkeleton />;
  }
  if (pathname.startsWith("/inspection-orders")) {
    return (
      <InspectionOrdersListShell>
        <InspectionOrdersListSkeleton count={4} />
      </InspectionOrdersListShell>
    );
  }
  if (pathname.startsWith("/document-library")) {
    return (
      <DocumentLibraryShell>
        <DocumentUploadSkeleton viewType="large" count={8} />
      </DocumentLibraryShell>
    );
  }
  if (pathname.startsWith("/locationmap")) {
    return <LocationMapSkeleton />;
  }
  if (pathname.startsWith("/select-company")) {
    return <CompanySelectionSkeleton />;
  }
  if (pathname.startsWith("/dashboard")) {
    return <CompanyDashboardSkeleton />;
  }
  if (pathname === "/workorders") {
    return (
      <ListPageCardShell
        searchPlaceholder="Search by Title or Description"
        addButtonLabel="Create Work Order"
      >
        <WorkOrdersListSkeleton />
      </ListPageCardShell>
    );
  }
  if (pathname.startsWith("/onboardingsessions")) {
    return (
      <ListPageCardShell searchPlaceholder="Search sessions..." addButtonLabel="Add New Session">
        <OnboardingListSkeleton />
      </ListPageCardShell>
    );
  }
  if (pathname.startsWith("/masterdatarequest")) {
    return (
      <ListPageCardShell searchPlaceholder="Search requests..." addButtonLabel="Create Request">
        <MasterDataRequestSkeleton />
      </ListPageCardShell>
    );
  }
  if (pathname.startsWith("/manufacturer")) {
    return masterDataListFallback("Search manufacturers...", "Add Manufacturer");
  }
  if (pathname.startsWith("/vendor")) {
    return masterDataListFallback("Search vendors...", "Add Vendor");
  }
  if (pathname.startsWith("/workorderstage")) {
    return masterDataListFallback("Search workorder stages...", "Add Workorder Stage");
  }
  if (pathname.startsWith("/team")) {
    return (
      <TeamPageShell>
        <TeamPageSkeleton count={6} />
      </TeamPageShell>
    );
  }
  if (pathname.startsWith("/billing")) {
    return <BillingPageSkeleton />;
  }
  if (pathname.startsWith("/subscription")) {
    return <SubscriptionOverviewSkeleton />;
  }
  if (pathname.startsWith("/ai-marketplace")) {
    return <AiMarketplaceSkeleton />;
  }
  if (pathname.startsWith("/task-list")) {
    return <TaskListSectionSkeleton />;
  }
  if (pathname === "/assets") {
    return <AssetListSkeleton />;
  }
  if (/^\/workorders\/(edit|view)\//.test(pathname)) {
    return <DetailFormSkeleton />;
  }
  if (/^\/assets\/(edit|view)\//.test(pathname)) {
    return <DetailFormSkeleton />;
  }

  return null;
}
